'use client'

import { useSignals } from '@preact/signals-react/runtime'
import Loader from '@susu/headless-commerce/components/Loader/Loader'
import { useClientNav } from '@susu/headless-commerce/features/navigation/components/ClientNavProvider'
import log from '@susu/headless-commerce/utils/log'
import { isNotUndefined } from '@susu/undefined'
import classNames from 'classnames'
import { useEffect, useRef } from 'react'
import { ClientFailed } from './ClientFailed'
import { ClientPopularSearchPhrases } from './ClientPopularSearchPhrases'
import { ClientSucceeded } from './ClientSucceeded'
import styles from './NavSearchResults.module.scss'

export type ClientNavSearchResultsProps = {
  className?: string
  searchPanelOpenedFrom: 'header' | 'menu'
}

export const ClientNavSearchResults = ({
  className,
  searchPanelOpenedFrom,
}: ClientNavSearchResultsProps) => {
  useSignals()
  const {
    debouncedSearchQuery,
    popularSearchQuery,
    searchState,
    searchSuggestionsQuery,
    debouncedTrackEvent,
  } = useClientNav()

  const searchResultsClass = classNames(
    styles['nav__search-results'],
    className,
  )

  const areSearchResultsEmpty =
    searchSuggestionsQuery.value.data?.searchSuggestions?.suggestedPhrases
      ?.length === 0 &&
    searchSuggestionsQuery.value.data?.searchSuggestions?.productSuggestions
      ?.length === 0 &&
    searchSuggestionsQuery.value.data?.searchSuggestions?.categorySuggestions
      ?.length === 0

  log.debug({
    method: 'ClientNavSearchResults',
    searchState: searchState.value,
    areSearchResultsEmpty,
  })

  const timeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    if (
      searchState.value !== 'succeeded' ||
      debouncedSearchQuery.value.length < 3
    ) {
      return
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      debouncedTrackEvent(debouncedSearchQuery.value)
    }, 500)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [searchState.value, debouncedSearchQuery.value, debouncedTrackEvent])

  if (searchState.value === 'loading') {
    return (
      <div className={searchResultsClass}>
        <Loader shade="dark" className="header-overlay" />
      </div>
    )
  }

  if (
    searchState.value === 'succeeded' &&
    areSearchResultsEmpty &&
    debouncedSearchQuery.value !== ''
  ) {
    return (
      <div className={searchResultsClass}>
        <ClientFailed searchQuery={debouncedSearchQuery.value} />
      </div>
    )
  }

  return (
    <div className={searchResultsClass}>
      {debouncedSearchQuery.value !== '' ? (
        <ClientSucceeded
          inputValue={debouncedSearchQuery.value}
          searchSuggestionsQuery={searchSuggestionsQuery.value}
          searchPanelOpenedFrom={searchPanelOpenedFrom}
        />
      ) : null}
      {isNotUndefined(popularSearchQuery.value.data) && (
        <ClientPopularSearchPhrases
          inputValue={debouncedSearchQuery.value}
          popularSearchPhrasesQuery={popularSearchQuery.value}
          searchPanelOpenedFrom={searchPanelOpenedFrom}
        />
      )}
    </div>
  )
}
